import type { CommunicationType } from '@src/constants'
import type { Currencies } from '@type/currency'

export const enum CountryCode {
  'GB' = 'GB',
  'US' = 'US',
  'CA' = 'CA',
  'AU' = 'AU',
  'IN' = 'IN',
  'global' = 'global',
}

export interface OrganizationSettings {
  softledger_sync_enabled?: boolean
  xero_sync_enabled?: boolean
  quickbooks_sync_enabled?: boolean
  netsuite_sync_enabled?: boolean
  country_code: CountryCode
  product_manual_input_enabled: boolean
}

export interface OrganizationIntegration {
  integration_id: number
  tenant_id: string
}
export interface Organization {
  id?: number
  name: string
  integration: OrganizationIntegration | null
  settings: OrganizationSettings
  currency: Currencies
  watchers: {
    users: number[]
    departments: number[]
    roles: number[]
  }
}
export interface OrganizationListItem {
  id: number
  name: string
  integration: OrganizationIntegration | null
  currency: Currencies
  watchers: {
    users: number[]
    departments: number[]
    roles: number[]
  }
  settings: OrganizationSettings
}

export interface ContactDetails {
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  website: string | null
  registration_number: string | null
}
export interface AdditionalFinanceDetails {
  bank_name?: string | null
  bank_address?: string | null
  crypto_address?: string | null
  crypto_currency?: string | null
  sort_code?: string | null
  account_type?: 'iban' | 'account_number' | null
}

export interface BankDetails {
  bank_address: string | null
  bank_zip: string | null
  bank_city: string | null
  bank_country: string | null
  bank_state: string | null
}
export interface Finance {
  id?: number | 'new'
  bank_account_name: string | null
  bank_account_number: string | null
  bank_account_details: string | null
  tax_id_number: string | null
  currency: string | null
  routing_number: string | null
  ifrs_code: string | null
  bank_clearing_code: string | null
  bank_details: BankDetails | null
  sort_code: string | null
  swift_code: string | null
  additional_data: AdditionalFinanceDetails | null
  country_code: string | null
}
export interface Address {
  address: string | null
  zip: string | null
  city: string | null
  country: string | null
  country_code: string | null
  state: string | null
}

export interface Contact {
  name: string
  account_number: string | null
  contact_details: ContactDetails | null
  files: File[] | string[]
  organization: number
  active_financial_details?: number | null
  financial_details?: Finance | null
  address_details: {
    billing: Address | null
    delivery: Address | null
  } | null
  id?: number
  xero_contact_id?: string
  settings?: { xero_contact_id: string | null; softledger_id: string | null }
  auth_user?: number | null
  invite_id?: number | null
  user_id?: number
  party?: ContactParty
  sync?: boolean
  visible?: boolean
}

export interface ContactListItem {
  name: string
  account_number: string | null
  id: number
  organization_id: number
  settings: {
    xero_contact_id: number | null
    softledger_id: number | null
    quickbooks_id: number | null
    netsuite_id?: string | null
    // Subsidiary internal-ids the vendor is enabled on in NetSuite OneWorld.
    // Set by the NetSuite vendor sync; absent/null/empty ⇒ unrestricted
    // (non-OneWorld realm, the column not exposed to the role, or the
    // vendor wasn't synced from NetSuite at all). The accounting_vendors
    // field on PR/PO/Bill forms filters its dropdown by this list against
    // the picked NetSuite Subsidiary.
    netsuite_subsidiaries?: string[] | null
    // Currency internal-ids the vendor accepts on NetSuite transactions —
    // from `vendorcurrency` (Multi-Currency Vendors), or `[primary]`
    // fallback. The Currency field on PR/PO/Bill forms filters its
    // dropdown by this list (intersected with the picked Subsidiary).
    // Absent/null/empty ⇒ unrestricted (vendor not synced from NS, or
    // defensive fallback for vendors with no primary currency).
    netsuite_currencies?: string[] | null
  }
  email?: string
  user_id?: number
  invite_id?: number | null
  bill_count: number
  invoice_count: number
  po_count: number
  quote_count: number
  visible: boolean
}

// contracts

export enum ContractStatuses {
  Active = 'active',
  Terminated = 'terminated',
  Expired = 'expired',
  NotActive = 'not_active',
}

export const enum ContactParty {
  vendor = 'vendor',
  customer = 'customer',
}

interface Contract {
  amount?: number | null
  auto_renewal: boolean
  contract_number: string
  currency?: string | null
  expiration_date?: string
  file_link?: string
  files: string[]
  manager: number
  name: string
  party?: ContactParty
  signing_date?: string
  start_date?: string
  status?: ContractStatuses
  team?: number
  terms_and_conditions?: string
}
export interface ContractRequest extends Contract {
  collaboration: number
  agreement_type?: number
  department?: number | null
}
export interface ContractResponse extends Contract {
  id: number
  collaboration: {
    name: string
    id: number
  }
  agreement_type?: {
    id: number
    name: string
    team: number
  }
  department?: {
    id: number
    name: string
  }
  created_at: string
}
export interface ContractListItemResponse {
  id: number
  vendor: {
    name: string
    id: number
  }
  status: ContractStatuses
  name: string
  expiration_date: string
  contract_number: string
  agreement_type: { id: number; team: number; name: string }
  party: ContactParty
}

export interface ContractListItem {
  id: number
  collaboration: {
    name: string
    id: number
  }
  status: ContractStatuses
  name: string
  expiration_date: string
  type: string
  contract_number: string
  agreement_type: { id: number; team: number; name: string }
  party: string
}

export interface ContractLineItemsResponse {
  results: ContractListItem[]
  count: number
}

export interface ContractFiltersRequest {
  status?: string
  collaboration?: number
  search?: string
  page?: number
  organization?: number
  size?: number
  agreement_type?: number
  contract_number?: string
  expiration_date_start?: string
  expiration_date_end?: string
  ordering?: string
}

export interface FilterOption {
  id: number
  name: string
}

export interface ContractFiltersResponse {
  agreement_types: FilterOption[]
  departments: FilterOption[]
  statuses: ContractStatuses[]
  collaborations: FilterOption[]
}

export enum ContractReminderFrequency {
  OncePerDay = 'once_per_day',
  OncePerWeek = 'once_per_week',
  OncePerMonth = 'once_per_month',
}

export enum ContractDeltaUnits {
  Day = 'day',
  Week = 'week',
  Month = 'month',
}

export interface ContractReminder {
  enabled: boolean
  communication?: CommunicationType
  frequency?: ContractReminderFrequency
  timedelta_before_expire?: string
}

export interface ContractTypeListItemResponse {
  name: string
  id: number
}

export interface ContractTypeRequest {
  name: string
}
export interface ContractTypeResponse {
  name: string
  id: number
}

// Discriminated union so the type system enforces what the runtime contract
// already requires: when ``ids === 'all'``, the server needs ``filters`` to
// know which population to act on (the team's full vendor pool would be
// surprising). The explicit-ids variant accepts no filters because the
// server short-circuits to id-scoped lookup.
export type ContactBulkAction =
  | {
      ids: number[]
      visible?: boolean
      filters?: never
    }
  | {
      ids: 'all'
      visible?: boolean
      // ``filters`` is required for the "all matching" path. ``Partial`` so
      // empty-filter (whole team) is still legal but explicit at the call
      // site — passing ``{}`` is allowed, omitting ``filters`` is not.
      filters: ContactListFilters
    }

export interface ContactListFilters {
  search?: string
  organization?: string[]
  currency?: string[]
  country?: string[]
  visible?: 'true' | 'false'
  has_financial_details?: 'true' | 'false'
  used_in_requests?: 'true' | 'false'
  // Integration types the "Synced with" column distinguishes. Values: xero,
  // softledger, quickbooks, netsuite, none. ``none`` = vendor not linked to
  // any external system.
  synced_with?: string[]
}

export interface ContactListFilterOption {
  value: string
  label: string
}

export interface ContactListFilterOptions {
  organizations: ContactListFilterOption[]
  currencies: ContactListFilterOption[]
  countries: ContactListFilterOption[]
  // Only includes integration types at least one vendor in the team's pool
  // is actually linked to — keeps the dropdown lean for clients that only
  // use one or two integrations.
  synced_with: ContactListFilterOption[]
}

export interface PaginatedContactsResponse {
  count: number
  next: string | null
  previous: string | null
  results: ContactListItem[]
}
